// @givmo/fundraisers-module — a fundraiser's cover chosen from the organization's Collateral (the KCC dev
// build, 2026-10-31: the appeal's cover was already in Collateral, and the cover step could only take
// a new upload of the same file). Beside Upload, on the wizard's photo step and the editor's cover.
//
// Two calls through the `givmoClient` hatch (the snapshot carries neither, `api-client.md` § A whole
// ROUTE the snapshot lacks): the library's current images (`GET /org/{ref}/profile/collateral`, the
// Profile → Collateral read, decoded by `@givmo/org-console`), and the act
// (`POST …/collateral/{item}/fundraiser-cover`), which the server copies through the fundraiser's own
// cover upload, so the rank, the freeze and the image checks are the upload's. The library's bucket
// serves no browser a cross-origin read, so the bytes never pass through here.
//
// Only while the read answers: dark (the route miss), or a person who doesn't read the library (403),
// or a library with no current image, offers nothing.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { givmoClient } from '@givmo/api-client';
import { Button, cn, testIdProp } from '@givmo/design-system';
import { decodeCollateralList, orgCollateralKey, type CollateralItem } from '@givmo/org-console';
import { DialogFrame } from './layer-dialogs';

export const COLLATERAL_COVER_COPY = {
  choose: 'Choose from Collateral',
  title: 'Choose a cover photo',
  body: 'Photos and graphics from your organization’s Collateral.',
  use: 'Use as cover',
  cancel: 'Cancel',
  failed: 'Couldn’t use this photo as the cover. Please try again.',
};
const C = COLLATERAL_COVER_COPY;

function base(orgRef: string): string {
  return `/org/${encodeURIComponent(orgRef)}/profile/collateral`;
}

/** The library's current images, newest first, or `null` where none can be offered. */
export function useCoverCollateral(orgRef: string | null): CollateralItem[] | null {
  const read = useQuery({
    queryKey: [...orgCollateralKey(orgRef ?? ''), 'cover-images'] as const,
    enabled: orgRef !== null,
    retry: false,
    queryFn: async ({ signal }) => {
      try {
        const { data } = await givmoClient.get({
          url: base(orgRef as string),
          query: { kind: 'image', status: 'current', limit: 100, offset: 0 },
          signal,
          throwOnError: true,
        });
        return decodeCollateralList(data).items.filter((item) => item.kind === 'image');
      } catch {
        // Dark here, or not this person's to read: nothing to offer, never an error on the cover.
        return [];
      }
    },
  });
  const items = read.data ?? [];
  return items.length > 0 ? items : null;
}

/** Make a Collateral image the fundraiser's cover; the server copies it through the cover upload. */
export async function applyCollateralCover(
  orgRef: string,
  itemId: string,
  fundraiserId: string,
): Promise<void> {
  await givmoClient.post({
    url: `${base(orgRef)}/${encodeURIComponent(itemId)}/fundraiser-cover`,
    body: { fundraiser_id: fundraiserId },
    throwOnError: true,
  });
}

/** The picker: the library's current images as a grid; one chosen, then "Use as cover". */
export function CollateralCoverDialog({
  open,
  onOpenChange,
  items,
  onChoose,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: readonly CollateralItem[];
  onChoose: (item: CollateralItem) => void;
}) {
  const [chosen, setChosen] = useState<string | null>(null);
  const item = items.find((candidate) => candidate.id === chosen) ?? null;
  return (
    <DialogFrame
      open={open}
      onOpenChange={(next) => {
        if (!next) setChosen(null);
        onOpenChange(next);
      }}
      title={C.title}
      testId="collateral-cover-dialog"
      width={640}
      sticky
      footer={
        <>
          <Button
            appearance="saas"
            tone="secondary"
            onClick={() => onOpenChange(false)}
            testId="collateral-cover-cancel"
          >
            {C.cancel}
          </Button>
          <Button
            appearance="saas"
            tone="primary"
            disabled={item === null}
            onClick={() => {
              if (item === null) return;
              setChosen(null);
              onChoose(item);
            }}
            testId="collateral-cover-use"
          >
            {C.use}
          </Button>
        </>
      }
    >
      <p className="text-s14 leading-h15 text-text-secondary">{C.body}</p>
      <div
        role="radiogroup"
        aria-label={C.title}
        className="grid grid-cols-2 gap-12 sm:grid-cols-3"
        {...testIdProp('collateral-cover-grid')}
      >
        {items.map((candidate) => {
          const selected = candidate.id === chosen;
          return (
            <button
              key={candidate.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setChosen(candidate.id)}
              className={cn(
                'flex min-w-0 flex-col gap-6 rounded-lg border p-6 text-left outline-none focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
                selected
                  ? 'border-brand-500 bg-brand-50 ring-1 ring-brand-500'
                  : 'border-border-subtle bg-surface hover:border-border',
              )}
              {...testIdProp(`collateral-cover-item-${candidate.id}`)}
            >
              <img
                src={candidate.url}
                alt=""
                className="aspect-[16/9] w-full rounded-md bg-surface-sunken object-cover object-[50%_30%]"
              />
              <span className="truncate px-2 text-s13 font-w500 text-text-strong">
                {candidate.title}
              </span>
            </button>
          );
        })}
      </div>
    </DialogFrame>
  );
}
